import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Standard copy affordance (#41), matching Desk's: the Material
 * `content_copy` icon, which becomes a `check` for ~2 s after a successful
 * copy, then reverts. One component, used everywhere a value is copied.
 *
 *  - Accessible: a real <button> (keyboard-operable, visible focus ring)
 *    labelled "Copy"; the outcome is announced through an
 *    `aria-live="polite"` region ("Copied" / the failure message), not by
 *    changing the button's own label.
 *  - Honest: if the Clipboard API is missing (insecure context) or rejects
 *    (permission denied) it shows an ERROR state — never a fake check — and
 *    the caller keeps the value selectable for a manual copy.
 */

export const COPIED_RESET_MS = 2000;
export const COPY_FAILED_RESET_MS = 4000;

export const COPY_FAILED_MESSAGE =
  "Couldn't copy automatically — select the value and copy it manually.";

type CopyState = 'idle' | 'copied' | 'failed';

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  'aria-hidden': true,
  focusable: false,
} as const;

// Material Symbols paths: content_copy, check, error.
const CopyIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
);
const CheckIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
  </svg>
);
const ErrorIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

export function CopyButton({
  value,
  label = 'Copy',
  className,
}: {
  value: string;
  /** Accessible name; describe WHAT is copied when several buttons share a view. */
  label?: string;
  className?: string;
}) {
  const [state, setState] = useState<CopyState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  // No state update after unmount, and no leaked timer.
  useEffect(() => clearTimer, []);

  const settle = (next: CopyState, resetMs: number) => {
    clearTimer();
    setState(next);
    timer.current = setTimeout(() => {
      timer.current = null;
      setState('idle');
    }, resetMs);
  };

  const copy = useCallback(async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(value);
      settle('copied', COPIED_RESET_MS);
    } catch {
      settle('failed', COPY_FAILED_RESET_MS);
    }
    // settle only touches refs/state setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <span className="copy-control">
      <button
        type="button"
        className={`btn btn-ghost copy-button${state === 'failed' ? ' copy-button-failed' : ''}${
          className ? ` ${className}` : ''
        }`}
        aria-label={label}
        data-state={state}
        onClick={() => void copy()}
      >
        {state === 'copied' ? <CheckIcon /> : state === 'failed' ? <ErrorIcon /> : <CopyIcon />}
      </button>
      <span className="copy-status" role="status" aria-live="polite">
        {state === 'copied' ? 'Copied' : state === 'failed' ? COPY_FAILED_MESSAGE : ''}
      </span>
    </span>
  );
}

/**
 * A value with its copy icon right next to it. The value stays selectable
 * (`user-select: all`) so a failed automatic copy can always be done by hand.
 */
export function CopyableValue({
  value,
  label = 'Copy',
}: {
  value: string;
  label?: string;
}) {
  return (
    <div className="copyable-value">
      <pre className="claim-code">{value}</pre>
      <CopyButton value={value} label={label} />
    </div>
  );
}
